import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    CharacterFilterParams,
    deleteCharacter,
    getCharacterById,
    saveCharacter,
    searchCharacters,
} from "@/api/CharacterApi.ts";

export const characterKeys = {
    all: ["characters"] as const,
    lists: () => [...characterKeys.all, "list"] as const,
    list: (filter: CharacterFilterParams, pageSize: number) => [...characterKeys.lists(), filter, pageSize] as const,
    detail: (id: string) => [...characterKeys.all, "detail", id] as const,
};

export function useCharacterQuery(id?: string) {
    return useQuery({
        queryKey: characterKeys.detail(id ?? ""),
        queryFn: ({signal}) => getCharacterById(id!, signal),
        enabled: Boolean(id),
    });
}

export function useCharactersQuery(filter: CharacterFilterParams, pageSize = 50) {
    const query = useInfiniteQuery({
        queryKey: characterKeys.list(filter, pageSize),
        initialPageParam: 0,
        queryFn: ({pageParam, signal}) => searchCharacters({page: pageParam, size: pageSize}, filter, signal),
        getNextPageParam: lastPage => {
            const nextPage = lastPage.page.number + 1;
            return nextPage < lastPage.page.totalPages ? nextPage : undefined;
        },
    });
    const characters = query.data?.pages.flatMap(page => page.content) ?? [];

    return {
        ...query,
        characters,
        totalCount: query.data?.pages[0]?.page.totalElements ?? 0,
        rowCount: characters.length + (query.hasNextPage ? 1 : 0),
        loadMore: async () => {
            if (query.hasNextPage) await query.fetchNextPage({cancelRefetch: false});
        },
    };
}

export function useDeleteCharacterMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteCharacter,
        onSuccess: (_, id) => {
            queryClient.removeQueries({queryKey: characterKeys.detail(id)});
            void queryClient.invalidateQueries({queryKey: characterKeys.lists()});
        },
    });
}

export function useSaveCharacterMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: saveCharacter,
        onSuccess: character => {
            queryClient.setQueryData(characterKeys.detail(character.id), character);
            void queryClient.invalidateQueries({queryKey: characterKeys.lists()});
        },
    });
}
